import { Tag, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { defaultLocale, supportedLocales } from "../config/locales";
import { contentLocaleLabel } from "../i18n/contentLocaleLabel";
import { isLocaleComplete, LocalizedEntity, translatedLocales } from "../utils/translations";

interface TranslationStatusProps<K extends string> {
    entity: LocalizedEntity<K>;
    /** Translatable field keys, in the same order the editor shows them. */
    fields: ReadonlyArray<K>;
}

/**
 * Table-cell summary of an entity's translation coverage: one tag per
 * non-default locale, filled when translated and greyed out when missing.
 */
export function TranslationStatus<K extends string>({ entity, fields }: TranslationStatusProps<K>) {
    const { t, i18n } = useTranslation();
    const present = new Set(translatedLocales(entity, fields));

    return (
        <>
            {supportedLocales
                .filter(locale => locale.code !== defaultLocale.code)
                .map(locale => {
                    const translated = present.has(locale.code);
                    const complete = translated && isLocaleComplete(entity, fields, locale.code);
                    const label = contentLocaleLabel(locale, i18n.language);
                    const title = !translated
                        ? t("translationStatus.missing", { locale: label })
                        : complete
                        ? t("translationStatus.translated", { locale: label })
                        : t("translationStatus.partial", { locale: label });

                    return (
                        <Tooltip key={locale.code} title={title}>
                            <Tag color={complete ? "green" : translated ? "orange" : "default"}>
                                {locale.code.toUpperCase()}
                            </Tag>
                        </Tooltip>
                    );
                })}
        </>
    );
}
